import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, CircularProgress, Chip, Switch, TextField,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead, TableRow, Alert,
} from '@mui/material';
import { Refresh, Save } from '@mui/icons-material';
import axios from 'axios';

// 🚀 Moon Ladder — статистика и настройки (slots-backend /api/admin/ladder).
const LADDER_API = 'https://slots.moonraid.online/api/admin/ladder';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const f2 = (x) => (Number.isFinite(+x) ? (+x).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : '—');
const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(2)}%`);
const profitColor = (x) => (x > 0 ? '#4caf50' : x < 0 ? '#ef5350' : '#fff');
const REASON = { cash: 'забрал', top: 'Луна', cap: '×500', auto: 'автосбор 24ч', hit: 'метеорит', expired: 'не начат 24ч' };

const Stat = ({ label, value, color, hint }) => (
  <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" sx={{ color: '#8fa1c4' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: color || '#fff', fontWeight: 800 }}>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: '#5b6b8c' }}>{hint}</Typography>}
    </CardContent>
  </Card>
);

const Period = ({ title, s }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>{title}</Typography>
    <Grid container spacing={1}>
      <Grid item xs={6} sm={3}><Stat label="Оборот" value={`${f2(s.turnover)}`} hint={`${s.rounds} подъёмов${s.active ? `, идут ${s.active}` : ''}`} /></Grid>
      <Grid item xs={6} sm={3}><Stat label="Профит казино" value={f2(s.profit)} color={profitColor(s.profit)} /></Grid>
      <Grid item xs={6} sm={3}><Stat label="RTP факт" value={pct(s.rtp)} hint="цель 93%" /></Grid>
      <Grid item xs={6} sm={3}><Stat label="Игроков / лучший икс" value={`${s.players} / ×${f2(s.bestMult)}`} hint={`в среднем забирают на ${f2(s.avgFloors)} этаже`} /></Grid>
    </Grid>
  </Box>
);

export default function LadderAdmin() {
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [period, setPeriod] = useState('today');
  const [sort, setSort] = useState('net');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, r, st] = await Promise.all([
        axios.get(`${LADDER_API}/stats`, { headers: authHeaders() }),
        axios.get(`${LADDER_API}/rounds`, { headers: authHeaders() }),
        axios.get(`${LADDER_API}/settings`, { headers: authHeaders() }),
      ]);
      if (s.data?.success) setStats(s.data);
      if (r.data?.success) setRounds(r.data.rounds);
      if (st.data?.success) setSettings((cur) => cur || st.data.settings);
    } catch (e) { /* тихо */ }
    setLoading(false);
  }, []);

  const loadPlayers = useCallback(async () => {
    try {
      const r = await axios.get(`${LADDER_API}/players`, { params: { period, sort }, headers: authHeaders() });
      if (r.data?.success) setPlayers(r.data.players);
    } catch (e) { /* тихо */ }
  }, [period, sort]);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axios.post(`${LADDER_API}/settings`, { settings }, { headers: authHeaders() });
      setMsg(r.data?.success ? { type: 'success', text: 'Сохранено, действует сразу' } : { type: 'error', text: r.data?.error || 'Ошибка' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Ошибка сохранения' });
    }
    setSaving(false);
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  const fe = stats?.features;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">🚀 Moon Ladder</Typography>
        <Chip size="small" label="только /ladder в боте" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<Refresh />} onClick={() => { load(); loadPlayers(); }}>Обновить</Button>
      </Box>

      {stats && (
        <>
          <Period title="Сегодня" s={stats.today} />
          <Period title="7 дней" s={stats.week} />
          <Period title="Всё время" s={stats.all} />

          <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Режимы (всё время)</Typography>
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Метеоритов</TableCell><TableCell align="right">Этажей</TableCell><TableCell align="right">Подъёмов</TableCell>
                <TableCell align="right">Оборот</TableCell><TableCell align="right">Профит</TableCell>
                <TableCell align="right">RTP факт</TableCell><TableCell align="right">Ср. этаж забора</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {stats.modes.map((m) => (
                  <TableRow key={m.stones}>
                    <TableCell>{m.stones}</TableCell><TableCell align="right">{m.floors}</TableCell>
                    <TableCell align="right">{m.rounds}</TableCell><TableCell align="right">{f2(m.turnover)}</TableCell>
                    <TableCell align="right" sx={{ color: profitColor(m.profit), fontWeight: 700 }}>{f2(m.profit)}</TableCell>
                    <TableCell align="right">{pct(m.rtp)}</TableCell><TableCell align="right">{f2(m.avgFloors)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {stats.regulator && (
            <Alert severity="info" sx={{ mb: 2, bgcolor: '#0e1424', color: '#cfe0ff' }}>
              Регулятор отдачи: факт-RTP окна <b>{pct(stats.regulator.actual)}</b> (последние {f2(stats.regulator.windowBet)} SPERM,
              {' '}{stats.regulator.windowRounds} подъёмов) → частота заносов сейчас <b>×{(+stats.regulator.lam).toFixed(2)}</b> от нормы
              {settings && !settings.ladder_rtp_regulator ? ' (регулятор выключен — всегда ×1)' : ''}
            </Alert>
          )}

          {fe && fe.rounds > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Золото ×2: ${fe.gold}`} sx={{ bgcolor: 'rgba(255,203,77,0.15)' }} />
              <Chip label={`Космос ×5: ${fe.cosmic}`} sx={{ bgcolor: 'rgba(200,110,255,0.15)' }} />
              <Chip label={`Лунный лифт: ${fe.lift}`} sx={{ bgcolor: 'rgba(141,108,255,0.15)' }} />
              <Chip label={`До Луны: ${fe.tops}`} sx={{ bgcolor: 'rgba(63,240,220,0.12)' }} />
              <Chip label={`Упёрлись в ×500: ${fe.caps}`} />
              <Chip label={`Автосбор 24ч: ${fe.autos}`} />
              <Chip label={`Брошены без хода: ${fe.expired}`} />
              <Chip label={`Срезал потолок выплаты: ${fe.payoutCapped}`} />
            </Box>
          )}
        </>
      )}

      <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Игроки</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={period} onChange={(e, v) => v && setPeriod(v)}>
          <ToggleButton value="today">Сегодня</ToggleButton><ToggleButton value="7d">7 дней</ToggleButton>
          <ToggleButton value="30d">30 дней</ToggleButton><ToggleButton value="all">Всё</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={sort} onChange={(e, v) => v && setSort(v)}>
          <ToggleButton value="net">Выиграли</ToggleButton><ToggleButton value="bet">Оборот</ToggleButton><ToggleButton value="rtp">RTP</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Игрок</TableCell><TableCell align="right">Оборот</TableCell><TableCell align="right">Итог игрока</TableCell>
            <TableCell align="right">RTP</TableCell><TableCell align="right">Подъёмов</TableCell><TableCell align="right">Лучший икс</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {players.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: '#5b6b8c' }}>Пока пусто</TableCell></TableRow>}
            {players.map((p) => (
              <TableRow key={p.userId}>
                <TableCell>{p.name} <Typography component="span" variant="caption" sx={{ color: '#5b6b8c' }}>{p.telegramId}</Typography></TableCell>
                <TableCell align="right">{f2(p.turnover)}</TableCell>
                <TableCell align="right" sx={{ color: profitColor(p.net), fontWeight: 700 }}>{f2(p.net)}</TableCell>
                <TableCell align="right">{pct(p.rtp)}</TableCell>
                <TableCell align="right">{p.rounds}</TableCell>
                <TableCell align="right">×{f2(p.best)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Последние подъёмы</Typography>
      <Box sx={{ overflowX: 'auto', mb: 3, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell>Когда</TableCell><TableCell>Игрок</TableCell><TableCell>Метеоритов</TableCell>
            <TableCell align="right">Ставка</TableCell><TableCell align="right">Этаж</TableCell><TableCell>Итог</TableCell>
            <TableCell align="right">Икс</TableCell><TableCell align="right">Выплата</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rounds.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{new Date(r.at).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow' })}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.stones}</TableCell>
                <TableCell align="right">{f2(r.bet)}</TableCell>
                <TableCell align="right">{r.floors}{r.golds > 0 ? ' 🟡' : ''}</TableCell>
                <TableCell>{r.status === 'active' ? 'идёт' : REASON[r.reason] || r.reason}</TableCell>
                <TableCell align="right">{r.status === 'won' ? `×${f2(r.mult)}` : '—'}</TableCell>
                <TableCell align="right" sx={{ color: r.status === 'active' ? '#8fa1c4' : profitColor(r.payout - r.bet) }}>{f2(r.payout)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {settings && (
        <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', maxWidth: 560 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Настройки</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Switch checked={!!settings.ladder_enabled} onChange={(e) => setSettings({ ...settings, ladder_enabled: e.target.checked })} />
              <Typography>{settings.ladder_enabled ? 'Игра включена' : 'Игра выключена'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Switch checked={!!settings.ladder_rtp_regulator} onChange={(e) => setSettings({ ...settings, ladder_rtp_regulator: e.target.checked })} />
              <Typography>{settings.ladder_rtp_regulator ? 'Мягкий регулятор отдачи включён' : 'Регулятор выключен (чистая математика 93%)'}</Typography>
            </Box>
            <Grid container spacing={1.5}>
              {[
                ['ladder_min_bet', 'Мин ставка', 0.1],
                ['ladder_max_bet', 'Макс ставка', 0.1],
                ['ladder_payout_cap', 'Макс выплата за подъём', 1],
                ['ladder_rtp_target', 'Регулятор: цель отдачи (0.93 = 93%)', 0.01],
                ['ladder_rtp_window_coins', 'Регулятор: окно, SPERM оборота', 100],
                ['ladder_rtp_gain', 'Регулятор: сила', 0.5],
                ['ladder_events_min', 'Заносы не реже (×нормы)', 0.05],
                ['ladder_events_max', 'Заносы не чаще (×нормы)', 0.05],
              ].map(([k, label, step]) => (
                <Grid item xs={6} key={k}>
                  <TextField fullWidth size="small" type="number" label={label} inputProps={{ step }}
                    value={settings[k] ?? ''} onChange={(e) => setSettings({ ...settings, [k]: e.target.value === '' ? '' : Number(e.target.value) })} />
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', color: '#5b6b8c', mt: 1 }}>
              Математика: RTP 93% при любой стратегии (лучшая — ровно 93%) при нормальной частоте заносов; заносы — золото ×2,
              космос ×5, лунный лифт +2 этажа, одно на подъём; потолок ×500. Мягкий регулятор (как в Авиаторе): по факт-RTP
              последних N SPERM меняет только частоту заносов нового подъёма: частота = 1 + сила × (цель − факт), в коридоре
              «не реже / не чаще». Иксы этажей не меняются. 0.7–1.15 ≈ отдача 90–95% у тех, кто забирает рано; выше 1.5 нельзя
              (отдача за 100%). RTP факт — по законченным подъёмам; брошенный подъём через 24 часа забирается сам.
            </Typography>
            {msg && <Alert severity={msg.type} sx={{ mt: 1.5 }}>{msg.text}</Alert>}
            <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving} sx={{ mt: 1.5 }}>Сохранить</Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
